import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

const sectionCardSx = {
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  backdropFilter: "blur(14px)",
  borderRadius: 2,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  p: 2,
};

const metricCardSx = {
  borderRadius: 2,
  border: "1px solid rgba(17,24,39,0.08)",
  background: "rgba(255,255,255,0.82)",
  p: 1.5,
  minWidth: 160,
};

function money(value) {
  const num = Number(value || 0);
  return `$${num.toFixed(2)}`;
}

export default function SalesReportPanel({
  title,
  roleLabel,
  rows = [],
  summary = {},
  loading = false,
  error = "",
  year,
  onYearChange,
  onExportCsv,
  onApproveRefund,
  onRejectRefund,
  actingPaymentId = null,
}) {
  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, idx) => String(currentYear - idx));
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={sectionCardSx}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: "#4b5563", mt: 0.5 }}>
              Review completed sales, pending refund approvals, and export a tax-oriented CSV report for your records.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Select
              size="small"
              value={year}
              onChange={(event) => onYearChange?.(event.target.value)}
              sx={{ minWidth: 140, backgroundColor: "#fff" }}
            >
              {years.map((optionYear) => (
                <MenuItem key={optionYear} value={optionYear}>
                  {optionYear}
                </MenuItem>
              ))}
            </Select>
            <Button variant="outlined" onClick={onExportCsv}>
              Export CSV
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
        <Box sx={metricCardSx}>
          <Typography variant="caption" color="text.secondary">Gross sales</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(summary.gross_total)}</Typography>
        </Box>
        <Box sx={metricCardSx}>
          <Typography variant="caption" color="text.secondary">{roleLabel} share</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {money(roleLabel === "Artist" ? summary.artist_total : summary.host_total)}
          </Typography>
        </Box>
        <Box sx={metricCardSx}>
          <Typography variant="caption" color="text.secondary">Platform fees</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(summary.platform_total)}</Typography>
        </Box>
        <Box sx={metricCardSx}>
          <Typography variant="caption" color="text.secondary">Pending refund approvals</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{summary.pending_refund_requests || 0}</Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={sectionCardSx}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading sales details…</Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sales records found for the selected year.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {rows.map((row) => (
              <Box
                key={row.payment_id}
                sx={{
                  border: "1px solid rgba(17,24,39,0.08)",
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: "rgba(255,255,255,0.9)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                      {row.artwork?.title || "Untitled"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#4b5563", mt: 0.5 }}>
                      Buyer: {row.buyer?.name || "Unknown"} | Sale date: {row.sale_date || "Unknown"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#4b5563", mt: 0.5 }}>
                      Status: <strong>{row.payment_status}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                    <Typography variant="body2" color="text.secondary">Gross</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(row.gross_amount)}</Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
                  <Typography variant="body2">Artist share: <strong>{money(row.artist_amount)}</strong></Typography>
                  <Typography variant="body2">Host share: <strong>{money(row.host_amount)}</strong></Typography>
                  <Typography variant="body2">Platform fee: <strong>{money(row.platform_fee)}</strong></Typography>
                </Stack>

                {row.refund_request_reason && (
                  <Typography variant="body2" sx={{ mt: 1, color: "#4b5563" }}>
                    Buyer refund reason: <strong>{row.refund_request_reason}</strong>
                  </Typography>
                )}
                {row.refund_rejection_reason && (
                  <Typography variant="body2" sx={{ mt: 0.75, color: "#991b1b" }}>
                    Seller rejection reason: <strong>{row.refund_rejection_reason}</strong>
                  </Typography>
                )}

                {row.payment_status === "refund_requested" && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "rgba(254, 243, 199, 0.7)",
                      border: "1px solid rgba(245, 158, 11, 0.25)",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#92400e", mb: 1 }}>
                      Refund approval required. The buyer has requested a refund and Stripe will not be called until you approve it.
                    </Typography>
                    {row.can_approve_refund && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button
                          variant="contained"
                          color="error"
                          disabled={actingPaymentId === row.payment_id}
                          onClick={() => onApproveRefund?.(row)}
                        >
                          {actingPaymentId === row.payment_id ? "Processing…" : "Approve refund"}
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={actingPaymentId === row.payment_id}
                          onClick={() => onRejectRefund?.(row)}
                        >
                          Reject refund
                        </Button>
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
